import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { useLocation } from 'react-router-dom';
import { useVisualViewport } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useAgentPanel } from '../Shell/AgentPanelContext';
import { hidesAgentBubble } from './agentBubbleRoutes';
import { linkedConversationId } from './agentLinks';

const Agent = lazy(() => import('./Agent'));

const SIZE_KEY = 'agent_panel_size';
const DEFAULT_WIDTH = 400;
const MIN_WIDTH = 320;
const MAX_WIDTH = 720;
// The page keeps at least this much room beside the docked panel.
const MIN_PAGE_WIDTH = 560;
const SWIPE_CLOSE_DISTANCE = 80;

type Drag = { x: number; start: number; width: number | null };

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, max));
}

// Storage can throw in a private window. The panel then opens at the default width.
function readWidth(): number | null {
  try {
    const size = JSON.parse(localStorage.getItem(SIZE_KEY) ?? 'null');
    if (Number.isFinite(size?.width)) return size.width;
  } catch {}
  return null;
}

function writeWidth(width: number | null) {
  try {
    if (width) {
      localStorage.setItem(SIZE_KEY, JSON.stringify({ width }));
    } else {
      localStorage.removeItem(SIZE_KEY);
    }
  } catch {}
}

export default function AgentBubble() {
  const { pathname, state, key } = useLocation();
  const { open, setOpen } = useAgentPanel();
  const [width, setWidth] = useState<number | null>(readWidth);
  const [dragOffset, setDragOffset] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<Drag | null>(null);
  const sheetDragRef = useRef<number | null>(null);
  const viewport = useVisualViewport();
  const close = useCallback(() => setOpen(false), [setOpen]);
  const linkedId = linkedConversationId(state);
  const hidden = hidesAgentBubble(pathname);
  const visible = open && !hidden;
  // A chat link from the full page opens the panel. The panel opens that chat from the same route state.
  useEffect(() => {
    if (hidden) {
      setOpen(false);
    } else if (linkedId) {
      setOpen(true);
    }
  }, [hidden, linkedId, key, setOpen]);

  // The page reflows beside the docked panel. Maps and charts measure on a window resize only.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--agent-panel-width', visible ? `${width ?? DEFAULT_WIDTH}px` : '0px');
    window.dispatchEvent(new Event('resize'));
    return () => {
      root.style.setProperty('--agent-panel-width', '0px');
    };
  }, [visible, width]);

  if (!visible) return null;

  const endResize = () => {
    const drag = dragRef.current;
    dragRef.current = null;
    // A click without a move saves nothing.
    if (!drag?.width) return;
    setWidth(drag.width);
    writeWidth(drag.width);
  };

  return (
    <div
      ref={panelRef}
      id="agent-panel"
      role="dialog"
      aria-label="Chat with Saarim"
      style={
        {
          '--panel-width': `${width ?? DEFAULT_WIDTH}px`,
          '--sheet-top': `${(viewport?.top ?? 0) + 56}px`,
          '--sheet-height': viewport ? `${viewport.height - 56}px` : 'calc(100dvh - 56px)',
          transform: dragOffset ? `translateY(${dragOffset}px)` : undefined,
        } as CSSProperties
      }
      className={cn(
        'fixed z-40 flex flex-col overflow-hidden bg-card transition-none',
        'max-lg:inset-x-0 max-lg:top-[var(--sheet-top)] max-lg:z-[60] max-lg:h-[var(--sheet-height)] max-lg:rounded-t-2xl',
        'shadow-[0_0_0_1px_hsl(0_0%_100%/0.08),0_-12px_32px_rgba(0,0,0,0.45)]',
        'lg:bottom-0 lg:right-0 lg:top-0 lg:w-[var(--panel-width)] lg:shadow-[0_0_0_1px_hsl(0_0%_100%/0.08)]',
        'motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-200 motion-safe:ease-out'
      )}
    >
      <div
        className="flex h-6 shrink-0 touch-none items-center justify-center lg:hidden"
        onPointerDown={(event) => {
          sheetDragRef.current = event.clientY;
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={(event) => {
          if (sheetDragRef.current === null) return;
          setDragOffset(Math.max(0, event.clientY - sheetDragRef.current));
        }}
        onPointerUp={() => {
          sheetDragRef.current = null;
          if (dragOffset > SWIPE_CLOSE_DISTANCE) close();
          setDragOffset(0);
        }}
        onPointerCancel={() => {
          sheetDragRef.current = null;
          setDragOffset(0);
        }}
      >
        <span aria-hidden className="h-[5px] w-10 rounded-full bg-white/30" />
      </div>
      <div
        aria-hidden
        title="Drag to resize. Double-click to reset."
        className="absolute inset-y-0 left-0 z-20 hidden w-1.5 cursor-ew-resize touch-none hover:bg-primary/40 lg:block"
        onPointerDown={(event) => {
          const panel = panelRef.current;
          if (!panel || event.button !== 0) return;
          event.preventDefault();
          event.currentTarget.setPointerCapture(event.pointerId);
          dragRef.current = { x: event.clientX, start: panel.getBoundingClientRect().width, width: null };
        }}
        onPointerMove={(event) => {
          const drag = dragRef.current;
          const panel = panelRef.current;
          if (!drag || !panel) return;
          drag.width = clamp(
            drag.start + drag.x - event.clientX,
            MIN_WIDTH,
            Math.min(MAX_WIDTH, window.innerWidth - MIN_PAGE_WIDTH)
          );
          // A state update on each move would render the whole transcript again. The width is state from pointerup on.
          panel.style.setProperty('--panel-width', `${drag.width}px`);
          document.documentElement.style.setProperty('--agent-panel-width', `${drag.width}px`);
        }}
        onPointerUp={endResize}
        onPointerCancel={endResize}
        onDoubleClick={() => {
          setWidth(null);
          writeWidth(null);
        }}
      />
      <Suspense
        fallback={
          <p role="status" className="p-6 text-center text-sm text-muted-foreground">
            Loading…
          </p>
        }
      >
        <Agent variant="panel" onClose={close} />
      </Suspense>
    </div>
  );
}
